import apiClient from './apiClient';

/**
 * Service for Rental Operations.
 * Handles fetching rental details, logs, and contract information using Rental IDs (Equipment IDs).
 */
const rentalService = {
    /**
     * Get full details for a specific Rental Asset.
     * Starts with Equipment ID -> Fetches Contract & Machine Details -> Resolves Technical ID for History.
     * @param {string} rentalId - The Equipment ID (e.g. EQ-B001)
     */
    async getRentalDetail(rentalId, contractId = null) {
        let url = `/rentals/${rentalId}/detail`;
        if (contractId) url += `?contractId=${contractId}`;
        return await apiClient.get(url);
    },

    /**
     * Get usage history (logs) for a rental asset.
     * The backend resolves the correct device_id internally.
     * @param {string} rentalId - The Equipment ID
     */
    async getRentalHistory(rentalId, limit = 1000, date = null) {
        let url = `/rentals/${rentalId}/history?limit=${limit}`;
        if (date) url += `&date=${date}`;
        return await apiClient.get(url);
    },

    /**
     * Get pre-aggregated daily summaries.
     */
    // One MONTH of daily logs per call (YYYY-MM; server defaults to the
    // current month) — past months are fetched only when the user pages back.
    async getDailySummary(rentalId, month) {
        const q = month ? `?month=${encodeURIComponent(month)}` : '';
        return await apiClient.get(`/rentals/${rentalId}/daily-summary${q}`);
    },

    /**
     * Get available log dates for a specific rental asset.
     */
    async getLogDates(rentalId) {
        return await apiClient.get(`/rentals/${rentalId}/log-dates`);
    },

    /**
     * Get active deployments overview.
     */
    async getActiveRentals() {
        return await apiClient.get('/rentals/active');
    },

    /**
     * Get available assets for rent.
     */
    async getAvailableAssets() {
        return await apiClient.get('/rentals/available');
    },

    /**
     * Rental-focused machine overview: identity + last rental + last service +
     * OEM spec sheet + operators. Powers the /rental-machine/:equipmentId page.
     * @param {string} equipmentId - The Equipment ID (or device/tracker id)
     */
    async getMachineOverview(equipmentId) {
        return await apiClient.get(`/rentals/machine/${encodeURIComponent(equipmentId)}/overview`);
    },

    // ---- Machine operators (many per On-Rent machine) ----
    async getOperators(equipmentId) {
        return await apiClient.get(`/rentals/machine/${encodeURIComponent(equipmentId)}/operators`);
    },
    async addOperator(equipmentId, payload) {
        return await apiClient.post(`/rentals/machine/${encodeURIComponent(equipmentId)}/operators`, payload);
    },
    async updateOperator(id, payload) {
        return await apiClient.put(`/rentals/operators/${id}`, payload);
    },
    async deleteOperator(id) {
        return await apiClient.delete(`/rentals/operators/${id}`);
    }
};

export default rentalService;
